// ─────────────────────────────────────────────
// KLOTHIQ — Bulk Import Template Generator
// Downloads an .xlsx scoped to one Product Type: fixed product +
// variant columns plus that type's dynamic attribute columns
// (spec FEAT-BULK-IMPORT-001 FR-002).
// ─────────────────────────────────────────────

import * as XLSX from 'xlsx';
import { buildImportColumns, columnHeaderLabel, sheetNameForType, ImportColumn } from './bulkImportColumns';
import type { Attribute, Category, TargetAudience, AgeGroup, Team } from '@/hooks/useMasterConfig';

export interface TemplateMasterConfig {
  categories: Category[];
  targetAudiences: TargetAudience[];
  ageGroups: AgeGroup[];
  teams: Team[];
}

export function downloadImportTemplate(
  productTypeName: string,
  productTypeCode: string,
  dynamicAttributes: Array<Attribute & { is_required?: boolean }>,
  masterConfig: TemplateMasterConfig
): void {
  const columns = buildImportColumns(dynamicAttributes);

  // ── Data sheet: header row only — the user fills rows below it ──
  const headerRow = columns.map(columnHeaderLabel);
  const dataWs = XLSX.utils.aoa_to_sheet([headerRow]);
  dataWs['!cols'] = columns.map((c) => ({ wch: Math.max(c.label.length + 4, 14) }));
  dataWs['!freeze'] = { xSplit: 0, ySplit: 1 };

  // ── Instructions sheet: what each column means + valid values pulled live ──
  const instructionRows: (string | number)[][] = [
    [`${productTypeName} — Import Instructions`],
    [],
    ['Column', 'Required', 'Notes'],
    ...columns.map((c) => [columnHeaderLabel(c), c.required ? 'Yes' : 'No', describeColumn(c)]),
    [],
    ['Valid Category names for this organization:'],
    ...(masterConfig.categories.length ? masterConfig.categories.map((c) => [c.name]) : [['(none configured yet)']]),
    [],
    ['Valid Target Audience names:'],
    ...(masterConfig.targetAudiences.length ? masterConfig.targetAudiences.map((a) => [a.name]) : [['(none configured yet)']]),
    [],
    ['Valid Age Group names:'],
    ...(masterConfig.ageGroups.length ? masterConfig.ageGroups.map((a) => [a.name]) : [['(none configured yet)']]),
    [],
    ['Valid Team names (if this product type uses Team):'],
    ...(masterConfig.teams.length ? masterConfig.teams.map((t) => [t.name]) : [['(none configured yet)']]),
  ];
  const instructionsWs = XLSX.utils.aoa_to_sheet(instructionRows);
  instructionsWs['!cols'] = [{ wch: 32 }, { wch: 10 }, { wch: 60 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, instructionsWs, 'Instructions');
  XLSX.utils.book_append_sheet(wb, dataWs, sheetNameForType(productTypeCode));

  const fileName = `klothiq-${productTypeCode.toLowerCase()}-import-template.xlsx`;
  XLSX.writeFile(wb, fileName);
}

function describeColumn(col: ImportColumn): string {
  if (col.key === 'product_code') return 'Leave blank to auto-generate a new product code. Fill in to update an existing product.';
  if (col.key === 'sku') return 'Leave blank to auto-generate. Fill in to update an existing variant (size/color must match exactly).';
  if (col.key === 'opening_stock') return 'Starting stock quantity. Ignored if this row updates an existing variant — use Inventory Adjustment for stock corrections.';
  if (col.optionsSource === 'teams') return 'Must exactly match a Team name from this organization\'s settings (see list below).';
  if (col.optionsSource === 'fixed' && col.fixedOptions) return `Must be one of: ${col.fixedOptions.join(', ')}`;
  if (col.key === 'category') return 'Must exactly match a Category name (see list below). Leave blank to skip.';
  if (col.key === 'target_audience') return 'Must exactly match a Target Audience name (see list below). Leave blank to skip.';
  if (col.key === 'age_group') return 'Must exactly match an Age Group name (see list below). Leave blank to skip.';
  if (col.key === 'size') return 'e.g. S / M / L / XL';
  if (col.key === 'default_cost' || col.key === 'default_price') return 'Number, in ₹, 0 or more.';
  return '';
}
